import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import client from "../../api/client";
import { AlertCircle, Loader, ArrowRight, ShieldCheck, LockKeyhole } from "lucide-react";

function toTitleCase(str) {
  if (!str) return str;
  return str
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Mirrors the 3-tier fee logic in ScholarshipController::initiate() — that's what actually gets
// charged, this just has to show the same number before the applicant pays. `currency` (NGN/USD)
// only tells us Nigeria vs not; the rest-of-Africa discount needs the full `region` value from
// GET /location, which the backend already returns correctly but this page wasn't reading.
function displayFee(scholarship, region) {
  if (region === "nigeria" && scholarship.application_fee_ngn)
    return `₦${Number(scholarship.application_fee_ngn).toLocaleString()}`;
  if (region === "africa" && scholarship.application_fee_africa_usd)
    return `$${scholarship.application_fee_africa_usd} USD`;
  if (scholarship.application_fee_usd)
    return `$${scholarship.application_fee_usd} USD`;
  if (scholarship.application_fee_ngn)
    return `₦${Number(scholarship.application_fee_ngn).toLocaleString()}`;
  return null;
}

function LockedField({ label, value }) {
  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label} <LockKeyhole size={10} color="#94a3b8" />
      </label>
      <div style={{ background: "#f1f5f9", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "11px 14px", fontSize: 14, color: "#64748b" }}>
        {value}
      </div>
    </div>
  );
}

export default function ScholarshipPay() {
  const { slug: pathSlug } = useParams();
  const [searchParams] = useSearchParams();

  const prefillName   = toTitleCase(searchParams.get("name")   || "");
  const prefillEmail  = searchParams.get("email")  || "";
  const prefillPhone  = searchParams.get("phone")  || "";
  const prefillCourse = toTitleCase(searchParams.get("course") || "");

  // GHL merge fields can't swap the URL path per-contact, so a `?scholarship=` query
  // param (matching a scholarship slug) takes priority over the path slug when present.
  // If the contact's GHL custom field is empty, GHL leaves the literal "{{contact.x}}"
  // placeholder in the URL instead of omitting it — reject anything that doesn't look
  // like a real slug so a blank merge field falls back to the path slug instead of 404ing.
  const looksLikeSlug = (v) => /^[a-z0-9]+(-[a-z0-9]+)*$/i.test(v);
  const rawQueryScholarship = searchParams.get("scholarship") || "";
  const queryScholarship = looksLikeSlug(rawQueryScholarship) ? rawQueryScholarship : "";
  const slug = queryScholarship || pathSlug || "";
  // Personalized = came from GHL email — email alone is enough to confirm
  const isPersonalized = !!prefillEmail;

  // Generic mode (no scholarship known from path or query): applicant picks from a dropdown
  const isGeneric = !slug;

  const [scholarship, setScholarship]     = useState(null);
  const [scholarships, setScholarships]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [fetchError, setFetchError]       = useState("");
  const [currency, setCurrency]           = useState(null);
  const [region, setRegion]               = useState(null);

  const [name,           setName]           = useState(prefillName);
  const [email,          setEmail]          = useState(prefillEmail);
  const [phone,          setPhone]          = useState(prefillPhone);
  const [selectedCourse, setSelectedCourse] = useState(prefillCourse);

  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState("");

  useEffect(() => {
    Promise.all([
      isGeneric ? client.get("/scholarships") : client.get(`/scholarships/${slug}`),
      client.get("/location"),
    ])
      .then(([schRes, geoRes]) => {
        if (isGeneric) {
          const list = schRes.data?.data || [];
          setScholarships(list);
        } else {
          setScholarship(schRes.data?.data || schRes.data);
        }
        setCurrency(geoRes.data?.currency || "NGN");
        setRegion(geoRes.data?.region || "nigeria");
      })
      .catch(() => {
        setFetchError(isGeneric ? "Could not load scholarships. Please try again." : "Could not load this scholarship. Please try again.");
        setCurrency("NGN");
        setRegion("nigeria");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleScholarshipChange = (e) => {
    const found = scholarships.find((s) => s.slug === e.target.value);
    setScholarship(found || null);
    setSelectedCourse("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");

    client
      .post("/scholarships/initiate", {
        scholarship: scholarship.slug,
        name,
        email,
        phone,
        selected_course: selectedCourse,
      })
      .then((res) => {
        const url = res.data?.payment_url;
        if (url) {
          window.location.href = url;
        } else {
          setFormError("Could not initiate payment. Please try again.");
          setSubmitting(false);
        }
      })
      .catch((err) => {
        setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
        setSubmitting(false);
      });
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
        <Loader size={32} color="#d15424" style={{ animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (fetchError || (!isGeneric && !scholarship) || (isGeneric && scholarships.length === 0)) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: "60px 24px", background: "#f8fafc" }}>
        <AlertCircle size={40} color="#d15424" />
        <p style={{ fontSize: 16, color: "#64748b", textAlign: "center" }}>
          {fetchError || (isGeneric ? "No scholarships are currently open for applications." : "Scholarship not found.")}
        </p>
      </div>
    );
  }

  const fee   = scholarship ? displayFee(scholarship, region) : null;
  const courses = scholarship?.courses || [];

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
        body { background: #f8fafc; }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 16px" }}>
        <div style={{ width: "100%", maxWidth: 460 }}>
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", boxShadow: "0 8px 40px rgba(0,0,0,0.06)", overflow: "hidden" }}>

            {/* Header */}
            <div style={{ background: "linear-gradient(135deg, #0f172a, #1e3a5f)", padding: "28px 32px" }}>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
                Scholarship Application
              </p>
              <h1 style={{ color: "#fff", fontSize: 20, fontWeight: 800, lineHeight: 1.3, fontFamily: "Montserrat, sans-serif" }}>
                {scholarship?.title || "Select a scholarship to apply"}
              </h1>
              <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {fee && (
                  <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(209,84,36,0.2)", border: "1px solid rgba(209,84,36,0.35)", borderRadius: 8, padding: "6px 14px" }}>
                    <span style={{ color: "#fb923c", fontSize: 14, fontWeight: 800 }}>Onboarding fee: {fee}</span>
                  </div>
                )}
                {scholarship?.onboarding_date && (
                  <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "6px 14px" }}>
                    <span style={{ color: "#cbd5e1", fontSize: 13 }}>Cohort starts: {scholarship.onboarding_date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Form */}
            <div style={{ padding: "28px 32px" }}>

              {isPersonalized && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", marginBottom: 20 }}>
                  <LockKeyhole size={13} color="#16a34a" style={{ flexShrink: 0 }} />
                  <p style={{ fontSize: 12, color: "#15803d", margin: 0 }}>
                    Your details are pre-filled. Just click the button below to proceed to payment.
                  </p>
                </div>
              )}

              {formError && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 20 }}>
                  <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                  <p style={{ fontSize: 13, color: "#dc2626" }}>{formError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* ── Personalized: all fields locked ─────────────────────── */}
                {isPersonalized ? (
                  <>
                    {name  && <LockedField label="Full Name"     value={name} />}
                    <LockedField label="Email Address" value={email} />
                    {phone && <LockedField label="Phone Number"  value={phone} />}
                    {prefillCourse ? (
                      <LockedField label="Course" value={prefillCourse} />
                    ) : courses.length > 0 && (
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Course
                        </label>
                        <select
                          required
                          value={selectedCourse}
                          onChange={(e) => setSelectedCourse(e.target.value)}
                          disabled={submitting}
                          style={{ width: "100%", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 14px", fontSize: 14, color: selectedCourse ? "#111827" : "#9ca3af", outline: "none", appearance: "none", opacity: submitting ? 0.6 : 1 }}
                          onFocus={(e) => (e.target.style.borderColor = "#d15424")}
                          onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                        >
                          <option value="" disabled>Select a course</option>
                          {courses.map((c) => (
                            <option key={c.id} value={c.title}>{c.title}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                ) : (
                  /* ── Generic: editable form ──────────────────────────────── */
                  <>
                    {isGeneric && (
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Scholarship
                        </label>
                        <select
                          required
                          value={scholarship?.slug || ""}
                          onChange={handleScholarshipChange}
                          disabled={submitting}
                          style={{ width: "100%", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 14px", fontSize: 14, color: scholarship ? "#111827" : "#9ca3af", outline: "none", appearance: "none", opacity: submitting ? 0.6 : 1 }}
                          onFocus={(e) => (e.target.style.borderColor = "#d15424")}
                          onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                        >
                          <option value="" disabled>Select a scholarship</option>
                          {scholarships.map((s) => (
                            <option key={s.slug} value={s.slug}>{s.title}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {[
                      { label: "Full Name",     type: "text",  value: name,  setter: setName,  placeholder: "John Doe" },
                      { label: "Email Address", type: "email", value: email, setter: setEmail, placeholder: "john@example.com" },
                      { label: "Phone Number",  type: "tel",   value: phone, setter: setPhone, placeholder: "+234 800 000 0000" },
                    ].map(({ label, type, value, setter, placeholder }) => (
                      <div key={label}>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {label}
                        </label>
                        <input
                          type={type}
                          required
                          value={value}
                          onChange={(e) => setter(e.target.value)}
                          placeholder={placeholder}
                          disabled={submitting}
                          style={{ width: "100%", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 14px", fontSize: 14, color: "#111827", outline: "none", opacity: submitting ? 0.6 : 1 }}
                          onFocus={(e) => (e.target.style.borderColor = "#d15424")}
                          onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                        />
                      </div>
                    ))}

                    {/* Course dropdown — only when no course in URL */}
                    {courses.length > 0 && (
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Course
                        </label>
                        <select
                          required
                          value={selectedCourse}
                          onChange={(e) => setSelectedCourse(e.target.value)}
                          disabled={submitting}
                          style={{ width: "100%", background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "11px 14px", fontSize: 14, color: selectedCourse ? "#111827" : "#9ca3af", outline: "none", appearance: "none", opacity: submitting ? 0.6 : 1 }}
                          onFocus={(e) => (e.target.style.borderColor = "#d15424")}
                          onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                        >
                          <option value="" disabled>Select a course</option>
                          {courses.map((c) => (
                            <option key={c.id} value={c.title}>{c.title}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}

                <button
                  type="submit"
                  disabled={submitting || (isGeneric && !scholarship)}
                  style={{ width: "100%", background: (submitting || (isGeneric && !scholarship)) ? "#9ca3af" : "#d15424", color: "#fff", fontWeight: 700, fontSize: 15, borderRadius: 12, padding: "14px 0", border: "none", cursor: (submitting || (isGeneric && !scholarship)) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4 }}
                >
                  {submitting ? (
                    <><Loader size={16} style={{ animation: "spin 0.8s linear infinite" }} /> Redirecting…</>
                  ) : (
                    <>{fee ? `Pay ${fee}` : "Proceed to Payment"} <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 20 }}>
                <ShieldCheck size={13} color="#10b981" />
                <span style={{ fontSize: 11, color: "#9ca3af" }}>Secure payment · Confirmation email sent after payment</span>
              </div>
            </div>
          </div>

          {currency && (
            <p style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginTop: 12 }}>
              {currency === "NGN" ? "Paying in Naira via Paystack" : "Paying in USD via Stripe"}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
